import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment/moment';
import { isEmpty, isEqual, isNil } from 'lodash';
import { ReactComponent as ArrowRight } from 'new/components/icons/arrowRight.svg';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { Box } from 'new/elements/Box';
import { Stack } from 'new/elements/Stack';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useAppContext } from 'new/hooks/useAppContext';
import { Utilization } from '../monitor/Utilization/Utilization';
import styles from './Dashboard.module.scss';
import cx from 'classnames';
import {
  addUserSettings,
  fetchAccountSummary,
  fetchDashboardMetrics,
  setContextualSupport,
  setContextualSupportVisibility,
  unsetContextualSupport,
  updateUserSettings,
} from 'js/actions';
import { SUPPORT_KEY_DASHBOARD, SUPPORT_KEY_FLOW_LIST } from 'js/constants/supportPageKeys';
import { MonthlyAccountUsage } from 'new/components/monitor/Utilization/components/MonthlyAccountUsage';
import { buildAccountUsageQuery } from 'new/components/monitor/Utilization/hooks/dateRangeFromQuery';
import { Button } from 'new/elements/Button';
import Typography from 'new/elements/Typography';
import { PRODUCT_TOURS_ENABLED, SITE_TITLE } from 'js/constants/config';
import QuickStartGuide from './QuickStartGuide/QuickStartGuide';
import { GET_USER_SETTINGS_REQUEST } from 'js/constants/actionTypes';
import {
  PRODUCT_TOURS_KEYS,
  QUICK_START_GUIDE_KEYS,
  USER_SETTINGS_KEYS,
} from 'js/constants/productTours';
import QuickStartPopup from './QuickStartPopup/QuickStartPopup';
import useEntities from 'new/state/entities/repository/useEntities';
import { ResourceTileGrid } from 'new/components/dashboard/components/ResourceTileGrid/ResourceTileGrid';
import { QuickCreateWidget } from './components/QuickCreateWidget/QuickCreateWidget';
import { WelcomeBanner } from './components/WelcomeBanner/WelcomeBanner';
import { FlowRunsWidget } from './components/FlowRunsWidget/FlowRunsWidget';
import { FlowRunsScreen } from '../monitor/Runs';
import { Notifications } from '../monitor/Notifications';
import { NotificationsWidget } from './components/NotificationsWidget/NotificationsWidget';

const QUICK_START_DEFAULT_SETTINGS = {
  [PRODUCT_TOURS_KEYS.quick_start_guide_completed]: false,
  [PRODUCT_TOURS_KEYS.quick_start_guide_progress]: {
    // [QUICK_START_GUIDE_KEYS.create_account]: true,
    [QUICK_START_GUIDE_KEYS.demo_videos]: false,
    [QUICK_START_GUIDE_KEYS.nexla_docs]: false,
    [QUICK_START_GUIDE_KEYS.set_notifications]: false,
    // [QUICK_START_GUIDE_KEYS.learning_center]: false,
    [QUICK_START_GUIDE_KEYS.create_flow]: false,
  },
};

export const FlowsKnowledgePanelContent = () => {
  const dispatch = useDispatch();
  const onClickOpenSupport = () => {
    dispatch(setContextualSupport(SUPPORT_KEY_FLOW_LIST));
    dispatch(setContextualSupportVisibility(true));
  };
  return (
    <Stack direction="row" gap="24px" padding="16px">
      <Stack gap="16px">
        <Typography variant="body2">
          The Data Flow Stats view is specifically designed for monitoring and identifying potential
          issues during data movement in the last 24 hours.
        </Typography>
        <Typography variant="body2">
          Each row displays total number records read and written out along with any read/write
          errors for that flow. Flows that might need your attention because of errors or
          significant changes in data volume are displayed at the top in red or yellow.
        </Typography>
        <a>
          <Button onClick={onClickOpenSupport} className={styles.flowsKnowledgeLink} variant="text">
            Explore Data Flows in detail
          </Button>
        </a>
      </Stack>
    </Stack>
  );
};

export default function Dashboard() {
  const history = useHistory();
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const [showGuide, setShowGuide] = useState(false);
  const [quickStartSteps, setQuickStartSteps] = useState({});
  const [settingId, setSettingId] = useState(null);
  const [popUpAnchorEl, setPopUpAnchorEl] = useState(undefined);
  const { setHeaderTitle } = useAppContext();
  const user = useCurrentUser();
  const { full_name: name, id, impersonated: impersonatedUser } = user;
  const { data: userSettings } = useEntities('userSettings');
  const isLoadingUserSettings = useSelector(
    (state: any) => state.request[GET_USER_SETTINGS_REQUEST],
  );
  const [usageDates, setUsageDates] = useState(() => ({
    startDate: moment().startOf('month').toDate(),
    endDate: moment().toDate(),
  }));
  const showPopUp = !!popUpAnchorEl;

  useEffect(() => {
    dispatch(setContextualSupport(SUPPORT_KEY_DASHBOARD));
    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_DASHBOARD));
    };
  }, []);

  useEffect(() => {
    dispatch(fetchAccountSummary(id));
    dispatch(fetchDashboardMetrics());
  }, []);

  // check for userSettings once mounted and set show guide, settingId, guide steps
  useEffect(() => {
    if (!impersonatedUser && isLoadingUserSettings === false) {
      if (isNil(userSettings) || isEmpty(userSettings)) {
        // create user_settings for quick start guide if there are none
        setShowGuide(true);
        dispatch(
          addUserSettings(USER_SETTINGS_KEYS.nexla_web_app, {
            ...QUICK_START_DEFAULT_SETTINGS,
          }),
        ).then((response) => {
          const newSettingId = response?.result;
          setSettingId(newSettingId);
        });
        setQuickStartSteps(
          QUICK_START_DEFAULT_SETTINGS[PRODUCT_TOURS_KEYS.quick_start_guide_progress],
        );
      } else {
        const setting = Object.keys(userSettings).find(
          (s) => userSettings[s].user_settings_type === USER_SETTINGS_KEYS.nexla_web_app,
        );
        if (setting) {
          // if user_settings, set local state with steps and guide status
          setUserSettings(userSettings[setting].settings, userSettings[setting].id);
          setSettingId(userSettings[setting].id);
        }
      }
    }
  }, [isLoadingUserSettings]);

  useEffect(() => {
    setHeaderTitle(`Welcome to ${SITE_TITLE}${name ? ', ' + name?.split?.(' ')?.[0] : ''}`);
  }, [user]);

  const setUserSettings = (settings, id) => {
    let guideStatus: boolean;
    // settings exist, set local state
    if (
      PRODUCT_TOURS_KEYS.quick_start_guide_progress in settings &&
      PRODUCT_TOURS_KEYS.quick_start_guide_completed in settings
    ) {
      guideStatus = !settings[PRODUCT_TOURS_KEYS.quick_start_guide_completed];
      setQuickStartSteps({
        ...settings[PRODUCT_TOURS_KEYS.quick_start_guide_progress],
      });
    } else if (
      // user has seen nx designer tour, don't show guide
      PRODUCT_TOURS_KEYS.tour_seen_nx_designer_upgrade in settings &&
      settings[PRODUCT_TOURS_KEYS.tour_seen_nx_designer_upgrade] === true
    ) {
      guideStatus = false;
      const allComplete =
        QUICK_START_DEFAULT_SETTINGS[PRODUCT_TOURS_KEYS.quick_start_guide_progress];
      Object.keys(allComplete).forEach((item) => (allComplete[item] = true));
      dispatch(
        updateUserSettings(id, {
          [PRODUCT_TOURS_KEYS.quick_start_guide_completed]: true,
          [PRODUCT_TOURS_KEYS.quick_start_guide_progress]: allComplete,
        }),
      );
    } else {
      // no user_guide settings, set to default settings
      guideStatus = true;
      dispatch(
        updateUserSettings(id, {
          ...QUICK_START_DEFAULT_SETTINGS,
        }),
      );
      setQuickStartSteps(
        QUICK_START_DEFAULT_SETTINGS[PRODUCT_TOURS_KEYS.quick_start_guide_progress],
      );
    }
    setShowGuide(guideStatus);
  };

  const updateSettings = (settings) => {
    const allStepsCompleted = Object.values(settings).every((item) => item === true);
    if (!isEqual(quickStartSteps, settings)) {
      setQuickStartSteps(settings);
      if (allStepsCompleted) {
        dispatch(
          updateUserSettings(settingId, {
            [PRODUCT_TOURS_KEYS.quick_start_guide_completed]: true,
            [PRODUCT_TOURS_KEYS.quick_start_guide_progress]: { ...settings },
          }),
        );
      } else {
        dispatch(
          updateUserSettings(settingId, {
            [PRODUCT_TOURS_KEYS.quick_start_guide_progress]: { ...settings },
          }),
        );
      }
    }
  };

  const onDismissGuide = () => {
    if (Object.values(quickStartSteps).every((item) => item === true)) {
      setPopUpAnchorEl('learning');
      setShowGuide(false);
    } else {
      setPopUpAnchorEl('guide');
    }
  };

  const setAllToTrue = () => {
    // need to set all steps to true, set popup, scroll to learning section
    const allComplete = { ...quickStartSteps };
    Object.keys(allComplete).forEach((item) => (allComplete[item] = true));
    updateSettings(allComplete);
    setPopUpAnchorEl('learning');
  };

  return (
    <div className={styles.root}>
      <div className={styles.contentContainer}>
        <div className={styles.content}>
          <WelcomeBanner />
          <QuickCreateWidget />
          <ResourceTileGrid />
          <Box id="usage" className={cx(styles.box)}>
            <MonthlyAccountUsage
              rootClassName={styles.monthlyUsage}
              dateRange={usageDates}
              onDatesChanged={(startDate, endDate) => setUsageDates({ startDate, endDate })}
              onSeeAllDetails={() =>
                history.push(
                  `${Utilization.screenConfig.path}?${buildAccountUsageQuery(usageDates)}#accountUsage`,
                )
              }
            />
          </Box>
          <Stack direction="row" gap="8px">
            <Box
              className={cx(styles.box, styles.flowRunsWidgetContainer)}
              actionTitle="View all Flow Runs"
              actionProps={{
                endIcon: <ArrowRight />,
                onClick: () => {
                  history.push(FlowRunsScreen.screenConfig.path as string);
                },
              }}
            >
              <FlowRunsWidget />
            </Box>
            <Box
              actionTitle="View all Notifications"
              actionProps={{
                endIcon: <ArrowRight />,
                onClick: () => {
                  history.push(Notifications.screenConfig.path as string);
                },
              }}
              className={cx(styles.notificationsWidgetContainer, styles.box)}
            >
              <NotificationsWidget />
            </Box>
          </Stack>
        </div>
        {/* {PRODUCT_TOURS_ENABLED === true &&
          isLoadingUserSettings === false &&
          showGuide === true ? (
          <div className={styles.quickStartGuide} id="guide">
            <QuickStartGuide
              quickStartSteps={quickStartSteps}
              updateSettings={updateSettings}
              onDismissGuide={onDismissGuide}
            />
          </div>
        ) : null}
        <QuickStartPopup
          anchorEl={popUpAnchorEl}
          isOpen={showPopUp}
          onClose={() => {
            setPopUpAnchorEl(undefined);
          }}
          setAllToTrue={setAllToTrue}
          setShowGuide={setShowGuide}
        /> */}
      </div>
    </div>
  );
}
